/**
 * CustomerNotesTab.tsx
 * Component độc lập hiển thị danh sách Nhật ký & Ghi chú CRM của khách hàng.
 */
import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Icon, type IconName } from '../Icon';
import { colors, spacing, fontWeights, borderRadius } from '../../theme';
import type { CustomerNote } from '../../data/customers';

export interface CustomerNotesTabProps {
  notes: CustomerNote[];
}

const NOTE_TYPE_META: Record<CustomerNote['type'], { label: string; icon: IconName; color: string; bg: string }> = {
  journal: { label: 'Nhật ký tư vấn', icon: 'description', color: '#003366', bg: 'rgba(0, 51, 102, 0.08)' },
  gift: { label: 'Tặng quà chăm sóc', icon: 'favorite', color: '#D97706', bg: 'rgba(217, 119, 6, 0.08)' },
  claims: { label: 'Hỗ trợ sự kiện BH', icon: 'medical_services', color: '#E11D48', bg: 'rgba(225, 29, 72, 0.08)' },
};

export const CustomerNotesTab: React.FC<CustomerNotesTabProps> = ({ notes }) => {
  if (!notes || notes.length === 0) {
    return (
      <View style={styles.emptyBox}>
        <Icon name="comment" size={32} color={colors.outlineVariant} />
        <Text style={styles.emptyText}>Chưa có ghi chú hoặc nhật ký tư vấn nào</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {notes.map((n) => {
        const meta = NOTE_TYPE_META[n.type] || NOTE_TYPE_META.journal;
        return (
          <View key={n.id} style={[styles.noteCard, { borderLeftColor: meta.color }]}>
            <View style={styles.noteHeader}>
              <View style={[styles.noteBadge, { backgroundColor: meta.bg }]}>
                <Icon name={meta.icon} size={13} color={meta.color} />
                <Text style={[styles.noteBadgeText, { color: meta.color }]}>{meta.label}</Text>
              </View>
              <Text style={styles.noteDate}>{n.date}</Text>
            </View>
            <Text style={styles.noteBody}>{n.content}</Text>
            <Text style={styles.noteAuthor}>— Người tạo: {n.author}</Text>
          </View>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: spacing[3],
  },
  emptyBox: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing[8],
    gap: spacing[2],
  },
  emptyText: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
  },
  noteCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.DEFAULT,
    padding: 14,
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    borderLeftWidth: 4,
    gap: spacing[1.5],
  },
  noteHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  noteBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1],
    paddingHorizontal: spacing[2],
    paddingVertical: 2,
    borderRadius: borderRadius.full,
  },
  noteBadgeText: {
    fontSize: 11,
    fontWeight: fontWeights.bold,
  },
  noteDate: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
  },
  noteBody: {
    fontSize: 13,
    color: colors.onSurface,
    lineHeight: 18,
  },
  noteAuthor: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    fontStyle: 'italic',
    alignSelf: 'flex-end',
  },
});
